import { useCallback, useMemo, useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import WorldMap, { type CountryContext, type ISOCode } from "react-svg-worldmap";
import { operational } from "@mideye/design-tokens";
import { SectionCard } from "@/components/DesignSystem";
import NotMeasured from "./NotMeasured.tsx";
import type { CountryOrigin } from "@/types";
import { countryName } from "@/utils/countryName.ts";

function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t;
}

interface CountryEntry {
  code: string;
  attempts: number;
  percentage: number;
}

type SortField = "attempt_count" | "ip_count";

interface AttackDistributionProps {
  /** Country totals over every hostile ASN, so percentages describe the whole
   *  attack surface rather than the ten origins the panels happen to show. */
  readonly countries: CountryOrigin[] | null;
  readonly metric: SortField;
}

export default function AttackDistribution({ countries, metric }: AttackDistributionProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const byCountry = useMemo(() => {
    const valueOf = (c: CountryOrigin) => (metric === "ip_count" ? c.ip_count : c.attempt_count);
    const total = (countries ?? []).reduce((sum, c) => sum + valueOf(c), 0) || 1;
    return (countries ?? [])
      .map((c): CountryEntry => ({
        code: c.country_code,
        attempts: valueOf(c),
        percentage: Math.round((valueOf(c) / total) * 1000) / 10,
      }))
      .sort((a, b) => b.attempts - a.attempts);
  }, [countries, metric]);

  const topCountries = byCountry.slice(0, 10);
  const maxPct = topCountries[0]?.percentage ?? 1;

  const attackTokens = operational.dashboard.attackDistribution;
  const modeKey = isDark ? "dark" : "light";
  const accentColor = attackTokens.accent[modeKey];
  const baseFill = attackTokens.baseFill[modeKey];
  const borderColor = attackTokens.border[modeKey];
  const trackColor = attackTokens.track[modeKey];

  if (countries === null) {
    return (
      <SectionCard title="Attack Distribution" sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <NotMeasured source="the hostile-traffic data this map is drawn from" />
      </SectionCard>
    );
  }

  return (
    <SectionCard title="Attack Distribution" sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
        Geographic distribution of {metric === "ip_count" ? "blocked IPs" : "blocked attempts"}
      </Typography>

      <Box
        sx={{
          containerType: "inline-size",
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <ResponsiveLayout
          byCountry={byCountry}
          topCountries={topCountries}
          maxPct={maxPct}
          metric={metric}
          accentColor={accentColor}
          baseFill={baseFill}
          borderColor={borderColor}
          trackColor={trackColor}
        />
      </Box>
    </SectionCard>
  );
}

function ResponsiveLayout({
  byCountry,
  topCountries,
  maxPct,
  metric,
  accentColor,
  baseFill,
  borderColor,
  trackColor,
}: {
  readonly byCountry: CountryEntry[];
  readonly topCountries: CountryEntry[];
  readonly maxPct: number;
  readonly metric: SortField;
  readonly accentColor: string;
  readonly baseFill: string;
  readonly borderColor: string;
  readonly trackColor: string;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        flex: 1,
        minHeight: 0,
        "@container (min-width: 900px)": {
          flexDirection: "row",
          gap: 3,
        },
      }}
    >
      <ChoroplethMap
        byCountry={byCountry}
        maxPct={maxPct}
        accentColor={accentColor}
        baseFill={baseFill}
        borderColor={borderColor}
      />
      <TopCountriesSidebar
        countries={topCountries}
        maxPct={maxPct}
        metric={metric}
        accentColor={accentColor}
        trackColor={trackColor}
      />
    </Box>
  );
}

function buildStyleFunction(
  opts: { byCountry: CountryEntry[]; maxPct: number; accentColor: string; baseFill: string; borderColor: string },
) {
  const lookup = new Map<string, CountryEntry>();
  for (const entry of opts.byCountry) lookup.set(entry.code.toLowerCase(), entry);

  return ({ countryCode, countryValue }: CountryContext): React.CSSProperties => {
    if (countryValue === undefined) {
      return { fill: opts.baseFill, stroke: opts.borderColor, strokeWidth: 0.4, strokeOpacity: 1, cursor: "default" };
    }
    const entry = lookup.get(countryCode.toLowerCase());
    const pct = entry?.percentage ?? 0;
    const t = Math.min(pct / opts.maxPct, 1);
    const opacity = lerp(0.25, 1, t);
    return {
      fill: alpha(opts.accentColor, opacity),
      stroke: opts.borderColor,
      strokeWidth: 0.4,
      strokeOpacity: 1,
    };
  };
}

function ChoroplethMap({
  byCountry,
  maxPct,
  accentColor,
  baseFill,
  borderColor,
}: {
  readonly byCountry: CountryEntry[];
  readonly maxPct: number;
  readonly accentColor: string;
  readonly baseFill: string;
  readonly borderColor: string;
}) {
  const mapData = useMemo(
    () => byCountry.map((e) => ({ country: e.code.toLowerCase() as ISOCode, value: e.attempts })),
    [byCountry],
  );

  const styleFunction = useMemo(
    () => buildStyleFunction({ byCountry, maxPct, accentColor, baseFill, borderColor }),
    [byCountry, maxPct, accentColor, baseFill, borderColor],
  );

  const [size, setSize] = useState(0);
  const measuredRef = useCallback((node: HTMLDivElement | null) => {
    if (!node) return;
    const ro = new ResizeObserver(([entry]) => {
      setSize(Math.floor(entry.contentRect.width));
    });
    ro.observe(node);
  }, []);

  return (
    <Box
      ref={measuredRef}
      sx={{
        flex: 3,
        minWidth: 0,
        overflow: "hidden",
        "& path:hover": { opacity: "1 !important", filter: "none !important" },
        /* Hide the library's styled PathTooltip overlay */
        "& > div > div > div": { visibility: "hidden !important", pointerEvents: "none" },
      }}
    >
      {size > 0 && (
        <Box sx={{ mt: "-5%", mb: "-5%" }}>
          <WorldMap
            color={accentColor}
            backgroundColor="transparent"
            borderColor={borderColor}
            size={size}
            data={mapData}
            styleFunction={styleFunction}
            frame={false}
            tooltipTextFunction={({ countryName }) => countryName}
            tooltipBgColor="transparent"
            tooltipTextColor="transparent"
          />
        </Box>
      )}
    </Box>
  );
}

function TopCountriesSidebar({
  countries,
  maxPct,
  metric,
  accentColor,
  trackColor,
}: {
  readonly countries: CountryEntry[];
  readonly maxPct: number;
  readonly metric: SortField;
  readonly accentColor: string;
  readonly trackColor: string;
}) {
  return (
    <Box
      sx={{
        width: "100%",
        flexShrink: 0,
        "@container (min-width: 900px)": {
          width: 260,
        },
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
        Top Countries by {metric === "ip_count" ? "IP Count" : "Blocked Attempts"}
      </Typography>
      {countries.map((entry) => (
        <Box key={entry.code} sx={{ mb: 1.25 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.25 }}>
            <Typography variant="caption" sx={{ fontWeight: 500 }}>
              {countryName(entry.code)}
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              {entry.percentage}%
            </Typography>
          </Box>
          <Box
            sx={{
              height: 6,
              borderRadius: 3,
              bgcolor: trackColor,
            }}
          >
            <Box
              sx={{
                width: `${(entry.percentage / maxPct) * 100}%`,
                height: "100%",
                borderRadius: 3,
                bgcolor: accentColor,
              }}
            />
          </Box>
        </Box>
      ))}
    </Box>
  );
}
